/**
 * Effects Bridge for Poker Reborn Effects Engine
 *
 * Connects the GameEventBus to the EffectsDirector via the EventEffectMapper.
 * This is the glue that makes game events trigger visual effects.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 3
 */

import { log } from '@/utils/log';
import type { GameEventName, GameEventPayload } from '../events/GameEvents';
import { gameEventBus } from '../events/GameEventBus';
import { effectsDirector } from '../director/EffectsDirector';
import { eventEffectMapper } from './EventEffectMapper';

// =============================================================================
// EFFECTS BRIDGE CLASS
// =============================================================================

/**
 * Bridges game events to visual effects.
 *
 * Subscribes to GameEventBus events, uses EventEffectMapper to convert
 * them to EffectCues, and sends those cues to the EffectsDirector.
 *
 * This is a singleton - use EffectsBridge.getInstance()
 */
export class EffectsBridge {
  private static _instance: EffectsBridge | null = null;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Unsubscribe functions for each event subscription */
  private _unsubscribers: (() => void)[] = [];

  /** Whether the bridge is currently active */
  private _active = false;

  /** Events we're subscribed to */
  private _subscribedEvents: Set<GameEventName> = new Set();

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): EffectsBridge {
    if (!EffectsBridge._instance) {
      EffectsBridge._instance = new EffectsBridge();
    }
    return EffectsBridge._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    if (EffectsBridge._instance) {
      EffectsBridge._instance.stop();
    }
    EffectsBridge._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Start the bridge - subscribe to game events.
   * Call this after registering mappings with EventEffectMapper.
   */
  start(): void {
    if (this._active) {
      log.effects.warning`EffectsBridge already started`;
      return;
    }

    log.effects.info`EffectsBridge starting...`;

    // Subscribe to all registered events
    const registeredEvents = eventEffectMapper.getRegisteredEvents();

    for (const event of registeredEvents) {
      this.subscribeToEvent(event);
    }

    this._active = true;
    log.effects.info`EffectsBridge started, subscribed to ${registeredEvents.length} events`;
  }

  /**
   * Stop the bridge - unsubscribe from all events.
   */
  stop(): void {
    if (!this._active) {
      return;
    }

    log.effects.info`EffectsBridge stopping...`;

    // Unsubscribe from all events
    for (const unsubscribe of this._unsubscribers) {
      unsubscribe();
    }

    this._unsubscribers = [];
    this._subscribedEvents.clear();
    this._active = false;

    log.effects.info`EffectsBridge stopped`;
  }

  /**
   * Check if the bridge is active.
   */
  get active(): boolean {
    return this._active;
  }

  /**
   * Get list of subscribed events.
   */
  get subscribedEvents(): GameEventName[] {
    return Array.from(this._subscribedEvents);
  }

  // ---------------------------------------------------------------------------
  // Subscription Management
  // ---------------------------------------------------------------------------

  /**
   * Subscribe to a specific event.
   */
  subscribeToEvent<E extends GameEventName>(event: E): void {
    if (this._subscribedEvents.has(event)) {
      return;
    }

    const handler = (data: GameEventPayload<E>) => {
      this.handleEvent(event, data);
    };

    const unsubscribe = gameEventBus.on(event, handler);
    this._unsubscribers.push(unsubscribe);
    this._subscribedEvents.add(event);

    log.effects.debug`EffectsBridge: subscribed to "${event}"`;
  }

  /**
   * Unsubscribe from a specific event.
   * Note: This currently removes all unsubscribers - would need tracking per event for selective removal.
   */
  unsubscribeFromEvent(_event: GameEventName): void {
    // For simplicity, we'd need a map of event -> unsubscribe function
    // to properly implement this. For now, this is a no-op.
    log.effects.warning`EffectsBridge: unsubscribeFromEvent not fully implemented`;
  }

  // ---------------------------------------------------------------------------
  // Event Handling
  // ---------------------------------------------------------------------------

  /**
   * Handle a game event by converting it to effects and playing them.
   */
  private handleEvent<E extends GameEventName>(
    event: E,
    data: GameEventPayload<E>
  ): void {
    // Get effect cues from the mapper
    const cues = eventEffectMapper.getEffects(event, data);

    if (cues.length === 0) {
      return;
    }

    log.effects.debug`EffectsBridge: "${event}" → ${cues.length} effect(s)`;

    // Play each cue through the director (fire-and-forget)
    for (const cue of cues) {
      // Add event tag for debugging/cancellation
      const taggedCue = {
        ...cue,
        tags: [...(cue.tags ?? []), `event:${event}`],
      };

      // Honor cue delay: stagger effect playback when delay > 0
      if (taggedCue.delay && taggedCue.delay > 0) {
        setTimeout(() => { void effectsDirector.play(taggedCue); }, taggedCue.delay);
      } else {
        void effectsDirector.play(taggedCue);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Manual Effect Triggering
  // ---------------------------------------------------------------------------

  /**
   * Manually trigger effects for an event without actually emitting it.
   * Useful for testing or manual effect triggering.
   */
  triggerEffectsFor<E extends GameEventName>(
    event: E,
    data: GameEventPayload<E>
  ): void {
    this.handleEvent(event, data);
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global effects bridge instance.
 */
export const effectsBridge = EffectsBridge.getInstance();

// =============================================================================
// INITIALIZATION HELPER
// =============================================================================

/**
 * Initialize the effects system.
 * Call this once during app startup after loading assets.
 */
export async function initializeEffectsSystem(): Promise<void> {
  // Import and register default mappings
  // Using dynamic import to avoid circular dependency issues
  const { registerDefaultMappings } = await import('./defaultMappings');
  registerDefaultMappings();

  // Wire up the EffectFactory to the EffectsDirector
  const { effectFactory } = await import('../director/EffectFactory');
  effectsDirector.setEffectFactory(effectFactory);

  // Register all effect creators (chips, player, timer, action, sound)
  const { registerAllCreators } = await import('../registry');
  await registerAllCreators();

  // Start the bridge
  effectsBridge.start();

  log.effects.info`Effects system initialized with ${effectFactory.getRegisteredTypes().length} creators`;
}

/**
 * Shutdown the effects system.
 * Call this during app cleanup.
 */
export function shutdownEffectsSystem(): void {
  effectsBridge.stop();
  effectsDirector.cancelAll();

  log.effects.info`Effects system shutdown`;
}
